import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Message, SelectItem } from 'primeng/api';
import { MessageService } from '@shared/services/message-service';
import { ProductService } from '../../../../services/product.service';
import { BehaviorSubject, combineLatest, Subject, Subscription, tap, filter } from 'rxjs';
import { ActivityBlockingService } from '@shared/services/activity-blocking.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { InvCountProductCount } from '../../../../models/inv-count-product-count';
import { InventoryService } from '../../../../services/inventory.service';
import { BayType } from '@shared/models/bay-type';
import { ShiftStatus } from '@shared/models/worked-shift';
import { CustomValidators } from '@shared/helpers/custom-validators';
import { ShiftContext } from '../../../../models/shift-context';
import { saveDebounce } from '@shared/helpers/save-debounce';

type SortLineForm = FormGroup<{
    id: FormControl<number>;
    displayOrder: FormControl<number>;
    productId: FormControl<number>;
    pieces1: FormControl<number>;
}>;

@Component({
    selector: 'app-sort-line-inventory',
    templateUrl: './sort-line-inventory.component.html',
    styleUrls: ['./sort-line-inventory.component.scss']
})
export class SortLineInventoryComponent implements OnInit, OnDestroy {

    @Input() invCountId: number;
    @Input() set subinventoryId(subinventoryId: number) {
        this.siteSubinventoryId.next(subinventoryId);
    }

    updatedInvProductCounts: InvCountProductCount[] = [];
    lastUpdatedProductCountId: number;
    sortLineInventoryForm = new FormGroup({
        productId: new FormControl<number>(null, Validators.required),
        pieces1: new FormControl<number>(0, [Validators.required, CustomValidators.numeric(false)])
    });
    products: SelectItem[] = [];
    subscriptions: Subscription[];
    shutdown: Subject<void> = new Subject<void>();
    siteId: string;
    context: ShiftContext;
    sortLineInventoryIsLoaded = false;
    sortLineInventoryIsSaved = false;
    siteSubinventoryId = new BehaviorSubject<number>(null);

    sortLineInventoryItems: SortLineForm[] = [];
    autoSaveFormSubscriptions: Subscription[] = [];

    readonly _sortLineInventoryItemNotCreatedMsg: Message = {
        severity: 'warn',
        summary: 'Could not Create Sort Line Inventory Item',
        detail: 'Fields are required.'
    };

    readonly _sortLineInventoryItemCreatedMsg: Message = {
        severity: 'success',
        summary: 'Sort Line Inventory Item Created',
        detail: ''
    };

    readonly _sortLineInventoryItemDeletedMsg: Message = {
        severity: 'warn',
        summary: 'Sort Line Inventory Item Deleted',
        detail: ''
    };

    constructor(
        private productService: ProductService,
        private shiftContextService: ShiftContextService,
        private messageService: MessageService,
        private inventoryService: InventoryService,
        private activityService: ActivityBlockingService) {
        }

    ngOnInit() {
        const siteObservable = this.shiftContextService.CurrentContext.pipe(tap(context => {
            this.context = context;

            if (context.status !== ShiftStatus.Open) {
                this.sortLineInventoryForm.disable();
                this.sortLineInventoryItems.forEach(form => form.disable({ emitEvent: false }));
            }
            else {
                this.sortLineInventoryForm.enable();
                this.sortLineInventoryItems.forEach(form => form.enable({ emitEvent: false }));
            }
        }));

        const productsObservable = this.productService.ProductsForSubinventory(this.siteSubinventoryId)
            .pipe(tap(products => {
                this.products = products.map(p => ({ value: p.id, label: p.label }));
            }));

        const sortInventoryObservable = this.inventoryService.CurrentInvProductCount.pipe(
            filter(counts => !!counts),
            tap(counts => {
                this.sortLineInventoryItems = counts
                    .filter(x => x.bayType === BayType.SortLine)
                    .map(item => this.addFormGroup(item));

                if (this.context?.status !== ShiftStatus.Open)
                    this.sortLineInventoryItems.forEach(form => form.disable({ emitEvent: false }));
                else
                    this.sortLineInventoryItems.forEach(form => form.enable({ emitEvent: false }));

                this.autoSaveFormSubscriptions.forEach(s => s.unsubscribe());
                this.autoSaveFormSubscriptions = this.sortLineInventoryItems.map(form => form.valueChanges
                    .pipe(saveDebounce(this.shutdown))
                    .subscribe(val => {
                        this.updateSortLine({
                            id: form.controls.id.value,
                            invCountId: this.invCountId,
                            productId: val.productId,
                            pieces1: val.pieces1,
                            displayOrder: form.controls.displayOrder.value,
                            bayType: BayType.SortLine
                        })
                    }));
            }));

        this.subscriptions = [
            combineLatest([siteObservable, productsObservable, sortInventoryObservable])
                .pipe(this.activityService.Wait('Sort Lines'))
                .subscribe()
        ];
    }

    ngOnDestroy() {
        this.shutdown.next();
        this.subscriptions.forEach(s => s.unsubscribe());
        this.autoSaveFormSubscriptions.forEach(s => s.unsubscribe());
        this.autoSaveFormSubscriptions = [];
    }

    updateSortLine(sortLineInventoryItem: InvCountProductCount) {
        if (+sortLineInventoryItem.pieces1 === 0) {
            sortLineInventoryItem.pieces1 = 0;
        }

        this.inventoryService.updateInvCountProductCount(sortLineInventoryItem).subscribe(_updated => {
            this.messageService.add({
                severity: 'success',
                summary: 'Repair Line Item Updated',
                detail: ''
            });
        });
    }

    createSortLine() {
        if (!this.sortLineInventoryForm.valid) {
            this.sortLineInventoryForm.markAllAsTouched();
            this.messageService.add(this._sortLineInventoryItemNotCreatedMsg);
        } else {
            const entry: InvCountProductCount = {
                id: 0,
                invCountId: this.invCountId,
                bayType: BayType.SortLine,
                displayOrder: this.sortLineInventoryItems.length
                    ? this.sortLineInventoryItems[this.sortLineInventoryItems.length - 1].controls.displayOrder.value + 1
                    : 0,
                productId: this.sortLineInventoryForm.value.productId,
                pieces1: +this.sortLineInventoryForm.value.pieces1
            };

            this.inventoryService.createInvCountProductCount(entry).subscribe(_created => {
                const form = this.addFormGroup(_created);
                this.sortLineInventoryItems = [ ...this.sortLineInventoryItems, form ];

                this.autoSaveFormSubscriptions.push(form.valueChanges
                    .pipe(saveDebounce(this.shutdown))
                    .subscribe(val => {
                        this.updateSortLine({
                            id: form.controls.id.value,
                            invCountId: this.invCountId,
                            productId: val.productId,
                            pieces1: val.pieces1,
                            displayOrder: form.controls.displayOrder.value,
                            bayType: BayType.SortLine
                        })
                    }));

                this.sortLineInventoryForm.reset({
                    productId: null,
                    pieces1: 0
                });
                this.messageService.add(this._sortLineInventoryItemCreatedMsg);
            });
        }
    }

    addFormGroup(item: InvCountProductCount) {
        return new FormGroup({
            id: new FormControl<number>(item.id),
            displayOrder: new FormControl<number>(item.displayOrder),
            productId: new FormControl<number>(item.productId, Validators.required),
            pieces1: new FormControl<number>(item.pieces1, Validators.required)
        });
    }

    deleteSortLine(sortLineInventoryItem: SortLineForm) {
        this.inventoryService.deleteInvCountProductCount(sortLineInventoryItem.controls.id.value).subscribe(() => {
            this.sortLineInventoryItems.splice(this.sortLineInventoryItems.indexOf(sortLineInventoryItem), 1);
            this.messageService.add(this._sortLineInventoryItemDeletedMsg);
        });
    }

    rowTrackBy(_i: number, item: SortLineForm): number {
        return item.controls.id.value;
    }
}
